import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  Clock,
  Target,
  Activity,
  BookOpen,
  UserX,
  UserCheck,
  Ticket,
  Timer,
  GraduationCap,
  Wrench,
  Tag
} from 'lucide-react';

interface FinancialMetrics {
  totalInvoiced: number;
  invoiceCount: number;
  totalCollected: number;
  paymentCount: number;
  collectionRate: number;
  overdueTotal: number;
  overdueCount: number;
  aging: {
    days30: number;
    days60: number;
    days90: number;
    days90Plus: number;
  };
  expectedIncome: {
    granularity: 'day' | 'month';
    expected: number;
    collected: number;
    pending: number;
    overdue: number;
    buckets: { key: string; expected: number; collected: number; pending: number }[];
  };
  dso: number;
}

interface AcademicMetrics {
  activeStudents: number;
  studentsByLevel: { level: string; count: number }[];
  inactiveStudents: number;
  churnRate: number;
  scheduledClasses: number;
  attendanceStats: Record<string, number>;
  attendanceRate: number;
  atRiskStudents: number;
  lowGradeStudents: number;
}

interface InfrastructureMetrics {
  ticketStats: Record<string, number>;
  totalTickets: number;
  openTickets: number;
  closedTickets: number;
  resolutionRate: number;
  mttr: number;
  topCategories: Array<{ name: string; count: number }>;
  priorityDistribution: Array<{ priority: string; count: number }>;
}

interface DocenteMetrics {
  gradeDistribution: Array<{ range: string; count: number }>;
  classesExecuted: number;
  classesScheduled: number;
  executionRate: number;
}

interface PromotionSummaryData {
  totalDiscounted: number;
  totalInvoicesWithPromotion: number;
  promotionCount: number;
  topPromotions: Array<{
    id: number;
    name: string;
    code: string;
    type: string;
    totalDiscounted: number;
    invoiceCount: number;
  }>;
}

interface DashboardData {
  period: { startDate: string; endDate: string };
  financial: FinancialMetrics;
  academic: AcademicMetrics;
  infrastructure: InfrastructureMetrics;
  docente: DocenteMetrics;
}

const LEVEL_LABELS: Record<string, string> = {
  carrera_profesional: 'Carrera profesional', curso_profesional: 'Curso profesional', curso_corto: 'Curso corto',
  clase_un_dia: 'Clase de un día', diplomado: 'Diplomado', licenciatura: 'Licenciatura',
  certificado: 'Certificado', especialidad: 'Especialidad', otro: 'Otro',
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatPercent = (value: number) => {
  return `${value.toFixed(1)}%`;
};

const COLORS = {
  green: '#10b981',
  yellow: '#f59e0b',
  red: '#ef4444',
  blue: '#3b82f6',
  purple: '#8b5cf6',
  orange: '#f97316',
  gray: '#6b7280'
};

export default function ExecutiveDashboard() {
  const [dateRange, setDateRange] = useState<'month' | 'ytd' | 'semester'>('month');
  
  const getDateRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    switch (dateRange) {
      case 'month':
        return {
          startDate: new Date(year, month, 1).toISOString().split('T')[0],
          endDate: new Date(year, month + 1, 0).toISOString().split('T')[0]
        };
      case 'ytd':
        return {
          startDate: new Date(year, 0, 1).toISOString().split('T')[0],
          endDate: now.toISOString().split('T')[0]
        };
      case 'semester':
        const semesterStart = month < 6 ? 0 : 6;
        return {
          startDate: new Date(year, semesterStart, 1).toISOString().split('T')[0],
          endDate: new Date(year, semesterStart + 6, 0).toISOString().split('T')[0]
        };
    }
  };

  const { startDate, endDate } = getDateRange();

  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['executive-dashboard', startDate, endDate],
    queryFn: async () => {
      const response = await axios.get(`/api/executive-dashboard/metrics?startDate=${startDate}&endDate=${endDate}`);
      return response.data;
    }
  });

  const { data: promoSummary } = useQuery<PromotionSummaryData>({
    queryKey: ['promotion-summary', startDate, endDate],
    queryFn: async () => {
      const response = await axios.get(`/api/promotions/summary?startDate=${startDate}&endDate=${endDate}`);
      return response.data;
    }
  });

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[600px] bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-500 text-sm">Cargando metricas...</p>
        </div>
      </div>
    );
  }

  const financial = data?.financial;
  const academic = data?.academic;
  const infrastructure = data?.infrastructure;
  const docente = data?.docente;

  const totalStudents = (academic?.activeStudents || 0) + (academic?.inactiveStudents || 0);
  const activePercent = totalStudents > 0 ? ((academic?.activeStudents || 0) / totalStudents) * 100 : 0;

  const gradeColors: Record<string, string> = {
    '90-100': COLORS.green,
    '80-89': COLORS.blue,
    '70-79': COLORS.yellow,
    '60-69': COLORS.orange,
    'Menos de 60': COLORS.red
  };

  const agingTotal = (financial?.aging.days30 || 0) + (financial?.aging.days60 || 0) + (financial?.aging.days90 || 0) + (financial?.aging.days90Plus || 0);

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Visor Ejecutivo 360</h1>
          <p className="text-gray-500 mt-1">Torre de Control - Salud financiera, academica y operativa</p>
        </div>
        
        <div className="inline-flex p-1 bg-gray-100 rounded-xl">
          {[
            { key: 'month', label: 'Mes Actual' },
            { key: 'ytd', label: 'YTD' },
            { key: 'semester', label: 'Semestre' }
          ].map((option) => (
            <button
              key={option.key}
              onClick={() => setDateRange(option.key as 'month' | 'ytd' | 'semester')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                dateRange === option.key 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 border-l-4 border-l-emerald-500 relative overflow-hidden">
          <DollarSign className="absolute -right-4 -bottom-4 w-24 h-24 text-emerald-500/10" />
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-500">Ingresos Facturados</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(financial?.totalInvoiced || 0)}</p>
            <p className="text-xs text-gray-400 mt-1">{financial?.invoiceCount || 0} facturas emitidas</p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-500 relative overflow-hidden">
          <TrendingUp className="absolute -right-4 -bottom-4 w-24 h-24 text-blue-500/10" />
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-500">Ingresos Cobrados</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(financial?.totalCollected || 0)}</p>
            <p className="text-xs text-gray-400 mt-1">{financial?.paymentCount || 0} pagos recibidos</p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 border-l-4 border-l-violet-500 relative overflow-hidden">
          <Target className="absolute -right-4 -bottom-4 w-24 h-24 text-violet-500/10" />
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 bg-gradient-to-br from-violet-400 to-violet-600 rounded-xl">
                <Target className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-500">Tasa de Cobranza</span>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-gray-900">{formatPercent(financial?.collectionRate || 0)}</p>
              {(financial?.collectionRate || 0) >= 80 ? (
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Saludable</Badge>
              ) : (financial?.collectionRate || 0) >= 60 ? (
                <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">Atencion</Badge>
              ) : (
                <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Critico</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 border-l-4 border-l-amber-500 relative overflow-hidden">
          <Clock className="absolute -right-4 -bottom-4 w-24 h-24 text-amber-500/10" />
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-500">DSO (Dias Cartera)</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{(financial?.dso || 0).toFixed(0)} dias</p>
            <p className="text-xs text-gray-400 mt-1">Tiempo promedio de cobro</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-white border border-gray-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-500" />
              Ingresos esperados del periodo
              <span title="Proyección de cobranza según los vencimientos de las cuotas de colegiatura dentro del rango de fechas seleccionado: lo ya cobrado más lo que falta por cobrar." className="text-xs text-gray-400 cursor-help border border-gray-300 rounded-full w-4 h-4 inline-flex items-center justify-center">?</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-2 text-center mb-4">
              {[
                { label: 'Esperado', value: financial?.expectedIncome?.expected, color: 'text-gray-900' },
                { label: 'Cobrado', value: financial?.expectedIncome?.collected, color: 'text-green-600' },
                { label: 'Por cobrar', value: financial?.expectedIncome?.pending, color: 'text-blue-600' },
                { label: 'Vencido', value: financial?.expectedIncome?.overdue, color: 'text-red-600' },
              ].map(k => (
                <div key={k.label}>
                  <p className={`text-base font-bold ${k.color}`}>{formatCurrency(k.value || 0)}</p>
                  <p className="text-xs text-gray-500">{k.label}</p>
                </div>
              ))}
            </div>
            <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {(financial?.expectedIncome?.buckets || []).length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">Sin cuotas con vencimiento en este rango</p>
              )}
              {(financial?.expectedIncome?.buckets || []).map(b => {
                const max = Math.max(...(financial?.expectedIncome?.buckets || []).map(x => x.expected), 1);
                return (
                  <div key={b.key} className="flex items-center gap-2 text-xs">
                    <span className="w-20 text-gray-500 shrink-0">{b.key}</span>
                    <div className="flex-1 bg-gray-100 rounded h-3 overflow-hidden flex">
                      <div className="bg-green-500 h-full" style={{ width: `${(b.collected / max) * 100}%` }} />
                      <div className="bg-blue-400 h-full" style={{ width: `${(b.pending / max) * 100}%` }} />
                    </div>
                    <span className="w-24 text-right font-medium text-gray-700">{formatCurrency(b.expected)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-green-500" />
              Alumnos activos por oferta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(academic?.studentsByLevel || []).length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">Sin inscripciones activas</p>
              )}
              {(academic?.studentsByLevel || []).map(l => {
                const max = Math.max(...(academic?.studentsByLevel || []).map(x => x.count), 1);
                return (
                  <div key={l.level} className="flex items-center gap-2 text-sm">
                    <span className="w-36 text-gray-600 shrink-0 truncate">{LEVEL_LABELS[l.level] ?? l.level}</span>
                    <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                      <div className="bg-emerald-500 h-full rounded" style={{ width: `${(l.count / max) * 100}%` }} />
                    </div>
                    <span className="w-10 text-right font-semibold text-gray-800">{l.count}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-3">Un alumno inscrito en varias ofertas cuenta en cada una.</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Cartera Vencida (Aging)
              <span title="Saldo pendiente de las cuotas de colegiatura vencidas, agrupado por días de retraso desde la fecha de vencimiento. Incluye recargo por mora cuando ya pasó la fecha límite y descuenta lo ya pagado." className="text-xs text-gray-400 cursor-help border border-gray-300 rounded-full w-4 h-4 inline-flex items-center justify-center">?</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">1 a 30 días</span>
                  <span className="text-sm font-medium text-yellow-600">{formatCurrency(financial?.aging.days30 || 0)}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500 rounded-full transition-all"
                    style={{ width: `${agingTotal > 0 ? ((financial?.aging.days30 || 0) / agingTotal) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">31 a 60 días</span>
                  <span className="text-sm font-medium text-orange-600">{formatCurrency(financial?.aging.days60 || 0)}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                  <div 
                    className="h-full bg-orange-500 rounded-full transition-all"
                    style={{ width: `${agingTotal > 0 ? ((financial?.aging.days60 || 0) / agingTotal) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">61 a 90 días</span>
                  <span className="text-sm font-medium text-red-500">{formatCurrency(financial?.aging.days90 || 0)}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                  <div 
                    className="h-full bg-red-400 rounded-full transition-all"
                    style={{ width: `${agingTotal > 0 ? ((financial?.aging.days90 || 0) / agingTotal) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">Más de 90 días</span>
                  <span className="text-sm font-medium text-red-600">{formatCurrency(financial?.aging.days90Plus || 0)}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                  <div 
                    className="h-full bg-red-500 rounded-full transition-all"
                    style={{ width: `${agingTotal > 0 ? ((financial?.aging.days90Plus || 0) / agingTotal) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="flex justify-between mt-4 pt-4 border-t text-sm">
              <span className="text-gray-500">Total vencido</span>
              <span className="font-semibold text-gray-900">{formatCurrency(agingTotal)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 border-l-4 border-l-green-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-green-500" />
              Poblacion Estudiantil
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="relative w-28 h-28">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                  <circle
                    cx="18"
                    cy="18"
                    r="15.5"
                    fill="none"
                    stroke="#fee2e2"
                    strokeWidth="3"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="15.5"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="3"
                    strokeDasharray={`${activePercent} ${100 - activePercent}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold text-gray-900">{totalStudents}</span>
                </div>
              </div>
              <div className="flex-1 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-gray-600">Activos</span>
                  </div>
                  <span className="font-bold text-green-600">{academic?.activeStudents || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserX className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-gray-600">Bajas</span>
                  </div>
                  <span className="font-bold text-red-600">{academic?.inactiveStudents || 0}</span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Tasa desercion</span>
                    <Badge className={`${
                      (academic?.churnRate || 0) < 5 
                        ? 'bg-green-100 text-green-700' 
                        : (academic?.churnRate || 0) < 15 
                          ? 'bg-yellow-100 text-yellow-700' 
                          : 'bg-red-100 text-red-700'
                    } hover:bg-inherit`}>
                      {formatPercent(academic?.churnRate || 0)}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-500" />
              Asistencia del Periodo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-3xl font-bold text-gray-900">{formatPercent(academic?.attendanceRate || 0)}</span>
                <Badge className={`${
                  (academic?.attendanceRate || 0) >= 90 
                    ? 'bg-green-100 text-green-700' 
                    : (academic?.attendanceRate || 0) >= 75 
                      ? 'bg-yellow-100 text-yellow-700' 
                      : 'bg-red-100 text-red-700'
                } hover:bg-inherit`}>
                  {(academic?.attendanceRate || 0) >= 90 ? 'Excelente' : (academic?.attendanceRate || 0) >= 75 ? 'Aceptable' : 'Bajo'}
                </Badge>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${Math.min(academic?.attendanceRate || 0, 100)}%`,
                    background: `linear-gradient(90deg, ${COLORS.green}, ${COLORS.yellow})`
                  }}
                ></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                <span className="text-gray-600">Presente: {academic?.attendanceStats?.presente || 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                <span className="text-gray-600">Ausente: {academic?.attendanceStats?.ausente || 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                <span className="text-gray-600">Tardanza: {academic?.attendanceStats?.tardanza || 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-gray-400"></div>
                <span className="text-gray-600">Justificado: {academic?.attendanceStats?.justificado || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-yellow-50 to-amber-50 border border-yellow-100 shadow-sm hover:shadow-lg transition-all duration-300">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-700">Alumnos en Riesgo</p>
                <p className="text-3xl font-bold text-amber-900 mt-1">{academic?.atRiskStudents || 0}</p>
                <p className="text-xs text-amber-600 mt-1">3+ inasistencias</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertTriangle className="w-8 h-8 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-rose-50 border border-red-100 shadow-sm hover:shadow-lg transition-all duration-300">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-700">Bajo Rendimiento</p>
                <p className="text-3xl font-bold text-red-900 mt-1">{academic?.lowGradeStudents || 0}</p>
                <p className="text-xs text-red-600 mt-1">Promedio menor a 60%</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <TrendingDown className="w-8 h-8 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 shadow-sm hover:shadow-lg transition-all duration-300">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-700">Tickets Abiertos</p>
                <p className="text-3xl font-bold text-blue-900 mt-1">{infrastructure?.openTickets || 0}</p>
                <p className="text-xs text-blue-600 mt-1">Pendientes de atender</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Ticket className="w-8 h-8 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 shadow-sm hover:shadow-lg transition-all duration-300">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-700">MTTR</p>
                <p className="text-3xl font-bold text-orange-900 mt-1">{(infrastructure?.mttr || 0).toFixed(1)}h</p>
                <p className="text-xs text-orange-600 mt-1">Tiempo resolucion</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Timer className="w-8 h-8 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 border-l-4 border-l-pink-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <Tag className="w-4 h-4 text-pink-500" />
              Descuentos por Promocion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl">
              <p className="text-xs font-medium text-pink-600 mb-1">Total descontado en el periodo</p>
              <p className="text-3xl font-bold text-pink-900">{formatCurrency(promoSummary?.totalDiscounted || 0)}</p>
              <div className="flex gap-4 mt-2 text-xs text-pink-700">
                <span>{promoSummary?.totalInvoicesWithPromotion || 0} recibos con promo</span>
                <span>·</span>
                <span>{promoSummary?.promotionCount || 0} promociones activas</span>
              </div>
            </div>

            {promoSummary?.topPromotions && promoSummary.topPromotions.length > 0 ? (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Top Promociones</p>
                <div className="space-y-3">
                  {promoSummary.topPromotions.map((promo, idx) => {
                    const maxDiscount = promoSummary.topPromotions[0]?.totalDiscounted || 1;
                    const pct = (promo.totalDiscounted / maxDiscount) * 100;
                    const medalColors = ['text-yellow-500', 'text-gray-400', 'text-orange-400'];
                    return (
                      <div key={promo.id}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`text-sm font-bold shrink-0 ${medalColors[idx] || 'text-gray-400'}`}>#{idx + 1}</span>
                            <span className="text-sm text-gray-700 truncate" title={promo.name}>{promo.name}</span>
                          </div>
                          <span className="text-sm font-semibold text-pink-700 ml-2 shrink-0">{formatCurrency(promo.totalDiscounted)}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-pink-400 to-rose-500 rounded-full transition-all duration-500"
                            style={{ width: `${Math.max(pct, 8)}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{promo.invoiceCount} recibo{promo.invoiceCount !== 1 ? 's' : ''}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-sm italic text-center py-4">Sin promociones aplicadas en el periodo</p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 border-l-4 border-l-orange-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-orange-500" />
              Infraestructura - Tickets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <p className="text-2xl font-bold text-gray-900">{infrastructure?.totalTickets || 0}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-xl">
                <p className="text-2xl font-bold text-green-700">{infrastructure?.closedTickets || 0}</p>
                <p className="text-xs text-green-600">Resueltos</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-xl">
                <p className="text-2xl font-bold text-blue-700">{formatPercent(infrastructure?.resolutionRate || 0)}</p>
                <p className="text-xs text-blue-600">Tasa resolucion</p>
              </div>
            </div>

            {infrastructure?.topCategories && infrastructure.topCategories.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Top Categorias de Fallas</p>
                <div className="space-y-2">
                  {infrastructure.topCategories.slice(0, 4).map((cat, idx) => (
                    <div key={cat.name} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
                        {idx + 1}
                      </span>
                      <div className="flex-1 flex items-center justify-between">
                        <span className="text-sm text-gray-600">{cat.name}</span>
                        <Badge variant="secondary" className="bg-gray-100">{cat.count}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 border-l-4 border-l-purple-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-purple-500" />
              Dimension Docente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-purple-700">Ejecucion del Plan Academico</span>
                <span className="text-2xl font-bold text-purple-900">{formatPercent(docente?.executionRate || 0)}</span>
              </div>
              <div className="w-full bg-purple-200 rounded-full h-2.5 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-violet-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(docente?.executionRate || 0, 100)}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-2 text-xs text-purple-600">
                <span>Ejecutadas: {docente?.classesExecuted || 0}</span>
                <span>Programadas: {docente?.classesScheduled || 0}</span>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Distribucion de Calificaciones</p>
              {docente?.gradeDistribution && docente.gradeDistribution.length > 0 ? (
                <div className="space-y-2">
                  {docente.gradeDistribution.map((grade) => {
                    const maxCount = Math.max(...(docente.gradeDistribution.map(g => g.count) || [1]));
                    const percentage = maxCount > 0 ? (grade.count / maxCount) * 100 : 0;
                    const color = gradeColors[grade.range] || COLORS.gray;
                    
                    return (
                      <div key={grade.range} className="flex items-center gap-3">
                        <span className="text-xs text-gray-600 w-20">{grade.range}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all flex items-center justify-end pr-2"
                            style={{ 
                              width: `${Math.max(percentage, 15)}%`,
                              backgroundColor: color
                            }}
                          >
                            <span className="text-white text-xs font-medium">{grade.count}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-400 text-sm italic text-center py-4">Sin datos de calificaciones en el periodo</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
